import { ismatch, ROUTE_CHANGE_EVENT, ORIGIN_PARAM, isAppPath, isOriginState, navigationTarget, routePath, stripQuery, dispatchRouteChange, } from './_common';
import { currentBlocker } from './blocker';
const parsePath = (value, basename) => {
    const _raw = value;
    const path = stripQuery(value);
    const withoutBase = basename && (path === basename || path.startsWith(`${basename}/`)) ? path.substring(basename.length) || '/' : path;
    const raw = withoutBase === '' ? '/' : withoutBase;
    const pieces = raw === '/' ? [] : raw.split('/').filter(Boolean);
    return { _raw, raw, pieces, ismatch: ismatch(pieces) };
};
// Where an entry sits in the tab's history, counted from the entry this document loaded on. Entries
// the router makes carry it, so a back or forward can be measured and undone. One made some other
// way (an in-page anchor) has none, and no move to or from it is measured.
const entryIndex = (state) => typeof state === 'object' && state !== null && 'index' in state && typeof state.index === 'number' ? state.index : null;
const entryState = () => typeof window.history.state === 'object' && window.history.state !== null ? window.history.state : {};
// A ?from= arriving with a document load leaves the address bar, and becomes this entry's origin when
// the link was followed in this tab, which leaves the page it came from behind this one. A tab the link
// opened starts a history of its own, with nothing behind it.
const adoptCarriedOrigin = () => {
    const url = new URL(window.location.href);
    const from = url.searchParams.get(ORIGIN_PARAM);
    if (from === null)
        return;
    url.searchParams.delete(ORIGIN_PARAM);
    window.history.replaceState(window.history.length > 1 ? Object.assign(Object.assign({}, entryState()), { from }) : entryState(), '', url.toString());
};
// A reload keeps the entry's number; a fresh load starts counting.
const numberFirstEntry = () => {
    if (entryIndex(window.history.state) !== null)
        return;
    window.history.replaceState(Object.assign(Object.assign({}, entryState()), { 'index': 0 }), '');
};
export const createHistoryRoutingStrategy = (basename) => {
    adoptCarriedOrigin();
    numberFirstEntry();
    // The route on screen. A back or forward has already moved the address by the time the page
    // hears of it, so a blocked one is stepped back by the same distance before any other listener
    // hears of it (this one is added first, before the app renders, and in the capture phase), and
    // the blocker is asked. Going after all repeats the move, unasked.
    let shown = { 'path': window.location.pathname, 'index': entryIndex(window.history.state) };
    let steppingBack = false;
    let agreed = false;
    window.addEventListener('popstate', event => {
        const blocker = agreed || steppingBack ? null : currentBlocker();
        const index = entryIndex(event.state);
        agreed = false;
        steppingBack = false;
        if (blocker !== null && index !== null && shown.index !== null && index !== shown.index && window.location.pathname !== shown.path) {
            event.stopImmediatePropagation();
            const distance = index - shown.index;
            steppingBack = true;
            window.history.go(-distance);
            blocker(() => {
                agreed = true;
                window.history.go(distance);
            });
            return;
        }
        shown = { 'path': window.location.pathname, index };
    }, { 'capture': true });
    // A blocked move between addresses with different anchors also announces the anchor it left.
    window.addEventListener('hashchange', event => {
        if (steppingBack)
            event.stopImmediatePropagation();
    }, { 'capture': true });
    return {
        'current': () => parsePath(window.location.pathname, basename),
        'parse': url => parsePath(new URL(url, window.location.origin).pathname, basename),
        'origin': () => {
            const state = window.history.state;
            return isOriginState(state) ? state.from : null;
        },
        'returnToOrigin': () => {
            if (!isOriginState(window.history.state))
                return false;
            window.history.back();
            return true;
        },
        'pathToURL': path => `${basename}${routePath(path)}`,
        'navigate': (target, options) => {
            const next = navigationTarget(target);
            if (next.kind === 'hash') {
                window.location.hash = next.value;
                return;
            }
            if (next.kind === 'document') {
                window.location.assign(next.value);
                return;
            }
            if (!isAppPath(window.location.pathname, basename) || !isAppPath(next.url.pathname, basename)) {
                window.location.assign(next.local);
                return;
            }
            // An origin carried in the link is recorded on the entry, never shown in the address bar.
            next.url.searchParams.delete(ORIGIN_PARAM);
            const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            const destination = `${next.url.pathname}${next.url.search}${next.url.hash}`;
            if (options === null || options === void 0 ? void 0 : options.replace) {
                window.history.replaceState(window.history.state, '', destination);
            }
            else if (destination !== current) {
                const index = entryIndex(window.history.state);
                window.history.pushState(index === null ? { 'from': current } : { 'from': current, 'index': index + 1 }, '', destination);
            }
            shown = { 'path': window.location.pathname, 'index': entryIndex(window.history.state) };
            dispatchRouteChange();
        },
        'listen': (listener) => {
            const onChange = () => listener();
            window.addEventListener('popstate', onChange);
            window.addEventListener('hashchange', onChange);
            window.addEventListener(ROUTE_CHANGE_EVENT, onChange);
            return () => {
                window.removeEventListener('popstate', onChange);
                window.removeEventListener('hashchange', onChange);
                window.removeEventListener(ROUTE_CHANGE_EVENT, onChange);
            };
        },
    };
};
